import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchPlanUndoStack, undoPlanChange } from '@/api/planUndo'
import { fetchTableUndoStack, undoTableChange } from '@/api/tableUndo'
import { queryKeys } from '@/lib/queryKeys'
import { toast } from '@/stores/useToastStore'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

/**
 * Merges the plan's two independent, per-user undo stacks (plan-header
 * changes and line-item table changes for the current version) into a
 * single "Undo" action that reverses whichever change is more recent.
 */
export function usePlanUndo(planId: string, versionId: string | undefined) {
  const queryClient = useQueryClient()

  const planStack = useQuery({
    queryKey: queryKeys.planUndoStack.byPlanId(planId),
    queryFn: () => fetchPlanUndoStack(planId),
    enabled: !!planId,
  })

  const tableStack = useQuery({
    queryKey: queryKeys.tableUndoStack.byVersion(planId, versionId ?? ''),
    queryFn: () => fetchTableUndoStack(planId, versionId!),
    enabled: !!planId && !!versionId,
  })

  const planTop = planStack.data?.entries[0]
  const tableTop = tableStack.data?.entries[0]

  const source: 'plan' | 'table' | null = !planTop
    ? tableTop
      ? 'table'
      : null
    : !tableTop
      ? 'plan'
      : new Date(tableTop.created_at) > new Date(planTop.created_at)
        ? 'table'
        : 'plan'

  const topEntry =
    source === 'plan' ? planTop : source === 'table' ? tableTop : undefined

  // Snapshot the entries that already existed when the plan/version was
  // opened, so callers can tell apart changes made during this session.
  const baselineKey = `${planId}:${versionId ?? ''}`
  const [baseline, setBaseline] = useState<{
    key: string
    ids: Set<string>
  } | null>(null)
  const stacksLoaded =
    planStack.isSuccess && (!versionId || tableStack.isSuccess)
  if (stacksLoaded && baseline?.key !== baselineKey) {
    setBaseline({
      key: baselineKey,
      ids: new Set(
        [
          ...(planStack.data?.entries ?? []),
          ...(tableStack.data?.entries ?? []),
        ].map(entry => entry.id)
      ),
    })
  }
  const hasSessionChanges =
    !!topEntry &&
    baseline?.key === baselineKey &&
    !baseline.ids.has(topEntry.id)

  const mutation = useMutation({
    mutationFn: () => {
      if (source === 'plan') return undoPlanChange(planId)
      if (source === 'table') return undoTableChange(planId, versionId!)
      throw new Error('Nothing to undo')
    },
    onSuccess: result => {
      toast.success(result.description || 'Change undone')

      if (source === 'plan') {
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.byId(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.metadata(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.assortmentPlans.all,
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.planUndoStack.byPlanId(planId),
        })
      } else if (source === 'table' && versionId) {
        useDagTableStore.getState().reset()
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.dagTable.metadata(planId, versionId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.planDetail.byId(planId),
        })
        queryClient.invalidateQueries({
          queryKey: queryKeys.tableUndoStack.byVersion(planId, versionId),
        })
      }
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Failed to undo change'))
      planStack.refetch()
      tableStack.refetch()
    },
  })

  return {
    topEntry,
    canUndo: !!topEntry,
    hasSessionChanges,
    isPending: mutation.isPending,
    undo: mutation.mutate,
  }
}
